import { useEffect, useRef } from 'react';

export interface ShortcutSpec {
  /** ปุ่มเดี่ยว (case-insensitive) เช่น 'j', 'Enter', '?', 'Escape' */
  key: string;
  /** คำอธิบายที่ไปแสดงใน KeyboardShortcutHelp — บังคับ ห้ามมีคีย์ลัดที่ไม่มีคำอธิบาย */
  description: string;
  /** กลุ่มใน KeyboardShortcutHelp */
  group?: string;
  handler: (event: KeyboardEvent) => void;
  /** ทำงานแม้โฟกัสอยู่ในช่องกรอก (ค่าเริ่มต้น: ไม่ทำงาน) */
  allowInInput?: boolean;
  disabled?: boolean;
}

const EDITABLE = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function inEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (EDITABLE.has(el.tagName)) return true;
  return el.isContentEditable === true;
}

/**
 * คีย์ลัดมาตรฐานของหลังบ้าน — ผูกที่ชั้น component ตั้งแต่แรก ไม่ใช่มาเติมทีหลัง
 *
 *   J / ArrowDown  เลื่อนลง          K / ArrowUp  เลื่อนขึ้น
 *   Enter          เปิดรายละเอียด    A            อนุมัติ
 *   R              ปฏิเสธ (เปิดช่องเหตุผล)
 *   /              โฟกัสช่องค้นหา    Esc          ปิด drawer/modal
 *   ?              เปิดหน้าช่วยเหลือคีย์ลัด
 *
 * ทุกคีย์ลัดต้องประกาศผ่าน hook นี้เพื่อให้ KeyboardShortcutHelp เก็บรายการได้เอง
 */
export function useKeyboardShortcuts(specs: ShortcutSpec[], enabled = true): void {
  const ref = useRef(specs);
  ref.current = specs;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      /**
       * ตอน embed อยู่ในเมนู admin ของ host หน้าจออื่นของ HIS
       * อยู่ใน DOM เดียวกัน ถ้าไม่กั้นตรงนี้ การกด A / J / K ที่หน้าอื่น
       * จะไปสั่งอนุมัติคำขอในคิว CRM โดยที่ผู้ใช้ไม่รู้ตัว
       *
       * ⚠️ ตรวจด้วย `.crm-bo` (class) ไม่ใช่ `#crm-bo` (id) โดยตั้งใจ
       *    โหมด standalone มี .crm-bo สองชั้น — ชั้นนอกครอบเมนูข้าง (AppShell) ด้วย
       *    ถ้าเช็คด้วย id จะบล็อกตอนโฟกัสอยู่ที่เมนูข้างของตัวเอง ซึ่งผิด (เจอตอนทดสอบจริง)
       *    ส่วนใน HIS หน้าจออื่นไม่ได้อยู่ใต้ .crm-bo เลย จึงยังกั้นได้ตามเจตนา
       */
      const focused =
        typeof document === 'undefined'
          ? null
          : ((event.target as Element | null) ?? document.activeElement);
      const inScope =
        !focused ||
        focused === document.body ||
        (typeof (focused as Element).closest === 'function' && !!(focused as Element).closest('.crm-bo'));
      if (!inScope) return;
      const pressed = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      for (const spec of ref.current) {
        if (spec.disabled) continue;
        const wanted = spec.key.length === 1 ? spec.key.toLowerCase() : spec.key;
        if (pressed !== wanted) continue;
        if (!spec.allowInInput && inEditable(event.target)) continue;
        event.preventDefault();
        spec.handler(event);
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

/** รายการคีย์ลัดกลางของคิวงาน — DataTable และ DecisionPanel อ้างชุดนี้ */
export const QUEUE_SHORTCUTS: Array<Omit<ShortcutSpec, 'handler'>> = [
  { key: 'j', description: 'เลื่อนไปรายการถัดไป', group: 'การเลื่อนรายการ' },
  { key: 'k', description: 'เลื่อนไปรายการก่อนหน้า', group: 'การเลื่อนรายการ' },
  { key: 'ArrowDown', description: 'เลื่อนไปรายการถัดไป', group: 'การเลื่อนรายการ' },
  { key: 'ArrowUp', description: 'เลื่อนไปรายการก่อนหน้า', group: 'การเลื่อนรายการ' },
  { key: 'Enter', description: 'เปิดรายละเอียดของรายการที่เลือก', group: 'การเลื่อนรายการ' },
  { key: 'a', description: 'อนุมัติรายการที่เปิดอยู่', group: 'การตัดสิน' },
  { key: 'r', description: 'ปฏิเสธ — เปิดช่องเหตุผล (บังคับกรอก)', group: 'การตัดสิน' },
  { key: '/', description: 'โฟกัสช่องค้นหา', group: 'ทั่วไป' },
  { key: 'Escape', description: 'ปิดแผงเลื่อนหรือหน้าต่างซ้อน', group: 'ทั่วไป' },
  { key: '?', description: 'เปิดรายการคีย์ลัดนี้', group: 'ทั่วไป' },
];
